import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

/**
 * Slice O · Language auto-detect banner.
 *
 * If the browser's preferred language differs from the persisted
 * `f2n_lang`, show a one-tap toast on first visit inviting the user to
 * switch. Records their choice so we never nag again — even a dismiss
 * counts as "seen".
 *
 * Only fires when:
 *   • `navigator.language` returns a supported code
 *   • it doesn't match the current I18nContext lang
 *   • the user hasn't already seen the prompt (`f2n_lang_prompt_seen`)
 */
const LANG_NAMES = {
  en: "English", hi: "हिन्दी", ta: "தமிழ்", te: "తెలుగు", bn: "বাংলা",
  mr: "मराठी", gu: "ગુજરાતી", pa: "ਪੰਜਾਬੀ", ur: "اردو", kn: "ಕನ್ನಡ",
  ml: "മലയാളം", or: "ଓଡ଼ିଆ", as: "অসমীয়া",
  es: "Español", fr: "Français", de: "Deutsch", pt: "Português",
  it: "Italiano", ru: "Русский", ja: "日本語", zh: "中文", ko: "한국어",
  ar: "العربية", th: "ไทย", vi: "Tiếng Việt", id: "Bahasa Indonesia",
  ms: "Bahasa Melayu", tr: "Türkçe",
};

function _shortCode(s) {
  return (s || "").toLowerCase().split(/[-_]/)[0].slice(0, 2);
}

export default function LanguageAutoDetect() {
  const { lang, setLang, supported } = useI18n();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (checked) return;
    // Only run once per browser install.
    try {
      if (localStorage.getItem("f2n_lang_prompt_seen") === "1") { setChecked(true); return; }
    } catch { /* ignore */ }

    const browser = _shortCode(typeof navigator !== "undefined" ? navigator.language : "");
    const current = _shortCode(lang);
    if (!browser || browser === current) { setChecked(true); return; }

    // If the app knows a supported catalogue and browser lang isn't in it,
    // skip silently rather than push into a language we can't render.
    if (Array.isArray(supported) && supported.length > 0
        && !supported.some(s => _shortCode(s.code || s) === browser)) {
      setChecked(true);
      return;
    }

    const label = LANG_NAMES[browser] || browser.toUpperCase();
    toast(`Switch to ${label}?`, {
      description: "Your browser's language differs from the one we're using.",
      duration: 12000,
      action: {
        label: "Switch",
        onClick: () => {
          setLang(browser);
          try { localStorage.setItem("f2n_lang_prompt_seen", "1"); } catch { /* ignore */ }
        },
      },
      cancel: {
        label: "Keep English",
        onClick: () => {
          try { localStorage.setItem("f2n_lang_prompt_seen", "1"); } catch { /* ignore */ }
        },
      },
      onDismiss: () => {
        try { localStorage.setItem("f2n_lang_prompt_seen", "1"); } catch { /* ignore */ }
      },
    });
    setChecked(true);
  }, [lang, supported, setLang, checked]);

  return null;
}
